
import { supabase } from "@/integrations/supabase/client";
import type { GameAttempt } from "@/lib/types/attempt";

export type { GameAttempt };

export async function validateAndProcessEmail(email: string): Promise<{
  existingAttempt?: GameAttempt;
  isExistingUser: boolean;
  error?: string;
}> {
  try {
    // Check if email exists
    const { data: existingEmails, error: emailError } = await supabase
      .from('emails')
      .select('email')
      .eq('email', email)
      .single();

    if (emailError && !emailError.message.includes('no rows')) {
      console.error('Error checking email:', emailError);
      return {
        isExistingUser: false,
        error: 'Failed to check email'
      };
    }

    const isExistingUser = !!existingEmails;
    console.log('Email check result:', { isExistingUser, email });

    // If it's an existing user, save it to ensure database consistency
    if (isExistingUser) {
      // Save email again to ensure it exists in both tables
      await saveNewEmail(email);
    }

    // Check for today's game attempt if it's an existing user
    if (isExistingUser) {
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      
      const { data: attemptData, error: attemptError } = await supabase
        .from('game_attempts')
        .select('*')
        .gte('created_at', today.toISOString())
        .order('created_at', { ascending: false })
        .limit(1);

      if (attemptError) {
        console.error('Error checking attempts:', attemptError);
        return {
          isExistingUser: true,
          error: 'Failed to check attempts'
        };
      }

      return {
        existingAttempt: attemptData?.[0] as GameAttempt | undefined,
        isExistingUser: true
      };
    }

    return {
      isExistingUser: false
    };
  } catch (error) {
    console.error('Error in validateAndProcessEmail:', error);
    return {
      isExistingUser: false,
      error: 'Failed to process email'
    };
  }
}

export async function saveNewEmail(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('emails')
      .insert([{ email }]);

    if (error) {
      // Check if it's a unique constraint violation
      if (error.code === '23505') {
        // This is not really an error - the email already exists
        return { success: true };
      }
      console.error('Error saving email:', error);
      return { 
        success: false, 
        error: 'Failed to save email' 
      };
    }

    return { success: true };
  } catch (error) {
    console.error('Error saving email:', error);
    return { 
      success: false, 
      error: 'Failed to save email' 
    };
  }
}
